import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import App from "./App.tsx";
import * as Sentry from "@sentry/react";
// const isEnabled =
//     window.location.hostname === "stg.ec.pepale-saibugas.com" ||
//     window.location.hostname === "onlineshop.saibugas.co.jp";
const isEnabled = window.location.hostname === "onlineshop.saibugas.co.jp";
// const isEnabled = window.location.hostname === "localhost";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  tracesSampleRate: 0, //  Capture 100% of the transactions
  sendDefaultPii: false,
  enabled: isEnabled,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // 全体的にはマスキングを無効にして、特定の要素のみマスキング
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false, // ← これを追加
      // 特定のセレクターでマスキングを制御
      mask: [
        // /orderページのフォーム入力要素のみマスキング
        '[data-page="order"] input[type="text"]',
        '[data-page="order"] input[type="email"]',
        '[data-page="order"] input[type="tel"]',
        '[data-page="order"] input[type="password"]',
        '[data-page="order"] textarea',
        '[data-page="order"] select',
        '[data-page="order"] [data-section="confirm"]',
      ],

      // ブロック要素は基本的に表示
      block: [],

      // 無視する要素（完全に記録しない）
      ignore: [
        // 必要に応じて機密性の高い要素を追加
      ],
    }),
  ],
  replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// ---- ここから画像監視ユーティリティ ----
// 送信するのは「いつまで経っても画像が表示されなかった」状態だけ。
// 誤送信を防ぐ: 意味のあるsrcのみ監視 / 報告時点のURLを使用 / 送信前に再チェック / 失敗した画像パスを特定可能に

declare global {
  interface Window {
    __imgWatchInstalled?: boolean;
    /** normalizedUrl -> 報告時点のフルURL（パス特定用） */
    __failedImages?: Map<string, string>;
    __imageErrorReportTimer?: ReturnType<typeof setTimeout>;
  }
}

const handled = new WeakSet<HTMLImageElement>();

/** 監視対象外: 空・placeholder・JSで後からセットする想定のimg */
function isMeaningfulImageSrc(url: string): boolean {
  if (!url || url.length < 5 || url === "#") return false;
  if (url.startsWith("data:")) return url.length >= 100; // ごく短いdataURIはplaceholderの可能性
  return true;
}

function normalizeUrlForGroup(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    u.search = "";
    return u.toString();
  } catch {
    return url.split("?")[0] || url;
  }
}

function watchImage(img: HTMLImageElement) {
  if (handled.has(img)) return;
  handled.add(img);

  if (img.complete && img.naturalWidth > 0) return;

  const initialUrl = img.currentSrc || img.src;
  if (!isMeaningfulImageSrc(initialUrl)) return; // 遅延表示・placeholderは監視しない

  let hasReported = false;

  /** 報告時点のURLを使って失敗リストに追加（誤ったパスを送らない） */
  const addToFailedList = () => {
    if (hasReported) return;
    hasReported = true;

    const urlAtReport = img.currentSrc || img.src;
    if (!isMeaningfulImageSrc(urlAtReport)) return; // 報告時点でも意味のないURLなら送らない

    if (!window.__failedImages) window.__failedImages = new Map<string, string>();
    const normalized = normalizeUrlForGroup(urlAtReport);
    window.__failedImages.set(normalized, urlAtReport); // フルURLを保持してパス特定可能に

    if (window.__imageErrorReportTimer) clearTimeout(window.__imageErrorReportTimer);

    window.__imageErrorReportTimer = setTimeout(() => {
      if (!window.__failedImages || window.__imageErrorReportTimer === undefined) return;

      // 送信直前: その後に読み込み完了したimgをリストから除外（誤送信防止）
      const stillFailing = new Map<string, string>();
      const nowCompleteNormals = new Set<string>();
      document.querySelectorAll("img").forEach((el) => {
        const i = el as HTMLImageElement;
        const src = i.currentSrc || i.src;
        const norm = normalizeUrlForGroup(src);
        if (i.complete && i.naturalWidth > 0) nowCompleteNormals.add(norm);
      });
      window.__failedImages.forEach((fullUrl, norm) => {
        if (!nowCompleteNormals.has(norm)) stillFailing.set(norm, fullUrl);
      });

      if (stillFailing.size === 0) {
        window.__failedImages.clear();
        return;
      }

      const failedCount = stillFailing.size;
      const failedPaths = Array.from(stillFailing.values()); // フルURLでパス特定可能
      const firstPath = failedPaths[0] ?? "(unknown)";

      Sentry.captureException(new Error(failedCount === 1 ? `Image failed to load: ${firstPath}` : `${failedCount} image(s) failed to load. First: ${firstPath}`), {
        level: "warning",
        tags: { asset: "img", count: failedCount.toString() },
        fingerprint: ["img-load-batch"],
        extra: {
          failedImages: failedPaths,
          failedCount,
          dpr: window.devicePixelRatio,
          ua: navigator.userAgent,
          screen: `${window.screen.width}x${window.screen.height}`,
        },
      });

      window.__failedImages.clear();
      window.__imageErrorReportTimer = undefined;
    }, 5000);
  };

  const timeoutId = setTimeout(() => {
    if (!img.complete) {
      addToFailedList();
    } else if (img.naturalWidth === 0) {
      addToFailedList();
    }
  }, 30000);

  img.addEventListener(
    "load",
    () => {
      clearTimeout(timeoutId);
      if (img.naturalWidth === 0) addToFailedList();
    },
    { once: true }
  );

  img.addEventListener("error", () => {}, { once: true });
}

function installImageWatchersOnce() {
  if (window.__imgWatchInstalled) return;
  window.__imgWatchInstalled = true;

  // 既存DOM上のimgに付与
  document.querySelectorAll("img").forEach((el) => watchImage(el as HTMLImageElement));

  // 以降に追加されるimgに対しても付与
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node instanceof HTMLImageElement) {
          watchImage(node);
        } else if (node instanceof HTMLElement) {
          node.querySelectorAll("img").forEach((img) => watchImage(img as HTMLImageElement));
        }
      });
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
}

// Sentry.init の直後に一度だけインストール
installImageWatchersOnce();

// ---- ここまで ----

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>
);
